/**
 * LessonGroundingPicker — Phase 119.A · Feb 2026.
 *
 * Reusable picker for attaching previously taught lessons to a
 * summative builder (Test / PT / Project). The picker surfaces the
 * teacher's recently-taught lessons, lets them filter by subject (the
 * spec calls for subject-first filtering), and lets them multi-select.
 *
 * The full lesson body is NEVER pulled to the client. The picker just
 * returns the selected lesson IDs to its parent; the backend hydrates
 * them server-side before injecting into the Claude prompt.
 */

import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronRight, Loader2, Search } from "lucide-react";
import { api } from "../lib/api";

export const LessonGroundingPicker = ({
  selectedIds = [],
  onChange,
  subjects = [],          // string[] — limits the picker to these subjects
  grade = "",             // optional — extra filter
  emptyHint = "No taught lessons in the selected subject yet. Generate one in the Generator first.",
  // Phase 119.D — when the picker is launched from an in-unit summative,
  // pre-attached lessons (from the parent unit's children) come in via
  // `selectedIds`. The picker collapses by default and shows just the
  // attached pills with a "+ Attach more from library" toggle. The
  // teacher can still expand to add other lessons from elsewhere.
  initialCollapsed = false,
  autoAttachedHint = "",
}) => {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [search, setSearch] = useState("");
  // Phase 119.D — `userExpanded` is null until the teacher clicks the
  // toggle. When null we defer to `initialCollapsed`, which may flip
  // from false → true after the auto-attach API call completes.
  const [userExpanded, setUserExpanded] = useState(null);
  const expanded = userExpanded !== null ? userExpanded : !initialCollapsed;
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        (subjects || []).forEach((s) => { if (s) params.append("subjects", s); });
        if (grade) params.append("grades", grade);
        const r = await api.get(`/lessons/for-grounding?${params.toString()}`);
        if (cancelled) return;
        setLessons(r.data?.lessons || []);
      } catch (_e) {
        if (!cancelled) setLessons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subjects, grade]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return lessons;
    return lessons.filter((l) =>
      (l.activity_name || "").toLowerCase().includes(needle) ||
      (l.tagline || "").toLowerCase().includes(needle) ||
      (l.strand || "").toLowerCase().includes(needle),
    );
  }, [lessons, search]);

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange && onChange(Array.from(next));
  };

  // Phase 119.D — render compact pills for auto-attached lessons when
  // we're in the initialCollapsed mode. The teacher needs to see them
  // at a glance + remove individual ones if they want.
  const attachedLessons = useMemo(() => {
    return selectedIds
      .map((id) => lessons.find((l) => l.id === id))
      .filter(Boolean);
  }, [lessons, selectedIds]);

  return (
    <div className="space-y-3" data-testid="lesson-grounding-picker">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#4a6984]/70">
            Ground in taught lessons
          </p>
          <p className="text-xs text-[#4a6984]/85">
            {autoAttachedHint || "Attach the lessons this summative should reflect. Builder reads the full lesson content."}
          </p>
        </div>
        <span
          className="ae-pill bg-[#96ded1]/30 text-[#1c2d3d]"
          data-testid="lesson-grounding-picker-selected-count"
        >
          {selectedIds.length} attached
        </span>
      </div>

      {/* Phase 119.D — compact attached-list when picker is collapsed. */}
      {initialCollapsed && attachedLessons.length > 0 ? (
        <div
          className="rounded-md border border-[#96ded1] bg-[#96ded1]/15 p-3 space-y-1.5"
          data-testid="lesson-grounding-picker-auto-list"
        >
          {attachedLessons.map((l) => (
            <div
              key={l.id}
              className="flex items-center justify-between gap-2 text-xs"
              data-testid={`lesson-grounding-picker-auto-${l.id}`}
            >
              <span className="font-bold text-[#1c2d3d] truncate">
                {l.activity_name}
              </span>
              <button
                type="button"
                onClick={() => toggle(l.id)}
                className="text-[10px] uppercase font-bold tracking-[0.12em] text-[#4a6984]/70 hover:text-[#1c2d3d]"
                data-testid={`lesson-grounding-picker-detach-${l.id}`}
              >
                Detach
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setUserExpanded(!expanded)}
        className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#1c9e7e] hover:text-[#1c2d3d]"
        data-testid="lesson-grounding-picker-expand-toggle"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {expanded ? "Hide library" : "Attach more from library"}
      </button>

      {expanded ? (
        <>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#4a6984]/60" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or strand…"
              className="w-full h-9 rounded-md border border-[#4a6984]/25 bg-white pl-8 pr-3 text-sm text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
              data-testid="lesson-grounding-picker-search"
            />
          </div>
          <div className="rounded-md border border-[#4a6984]/15 bg-white max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-xs text-[#4a6984]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading your lessons…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-xs italic text-[#4a6984]/75">
                {search.trim()
                  ? `No lessons match “${search.trim()}”.`
                  : emptyHint}
              </div>
            ) : (
              <ul className="divide-y divide-[#4a6984]/10">
                {filtered.map((l) => {
                  const sel = selectedSet.has(l.id);
                  return (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => toggle(l.id)}
                        data-testid={`lesson-grounding-picker-row-${l.id}`}
                        aria-pressed={sel}
                        className={`w-full text-left px-3 py-2 flex items-start gap-2.5 hover:bg-[#f4f5f7] ${sel ? "bg-[#96ded1]/15" : ""}`}
                      >
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${sel ? "border-[#1c9e7e] bg-[#1c9e7e] text-white" : "border-[#4a6984]/40 bg-white"}`}
                        >
                          {sel ? <Check className="h-2.5 w-2.5" /> : null}
                        </span>
                        <span className="flex-1 min-w-0">
                          <p className="font-bold text-sm text-[#1c2d3d] truncate" title={l.activity_name}>
                            {l.activity_name}
                          </p>
                          <p className="text-[10px] text-[#4a6984]/75 truncate">
                            {[l.output_type === "unit" ? "Unit" : "Lesson", l.grade, l.subject, l.strand].filter(Boolean).join(" · ") || "—"}
                          </p>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default LessonGroundingPicker;
